import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AnalysisSubHeader, BrandIdentity, BrandTabs } from '../../../../components';
import type { CreativeLandscapeBrand } from '../../CreativeLandscape.types';
import type {
  CreativeBrandContent,
  CreativeLandscape,
} from '../../model/creativeLandscapeData.schema';
import { higherOnAxis } from '../../model/positioning';
import { BrandDetail, type AxisComparison } from './BrandDetail';

interface BrandsAnalysisProps {
  creative: CreativeLandscape;
  brands: CreativeLandscapeBrand[];
  /** Print mode: every brand is stacked, since a PDF cannot operate the tab strip. */
  exporting?: boolean;
}

/**
 * Which competitors out-index this brand on each axis. The blob carries only the scores, so the
 * comparison the design shows under the positioning sentence is derived here.
 */
function axisComparisons(creative: CreativeLandscape, advertiser: string): AxisComparison[] {
  const { axisX, axisY, brands } = creative.positioning;
  return [
    { label: axisX.highLabel, advertisers: higherOnAxis(brands, advertiser, 'x') },
    { label: axisY.highLabel, advertisers: higherOnAxis(brands, advertiser, 'y') },
  ];
}

/**
 * One brand's detail. The tab strip already names the brand it is showing, so the block
 * carries no heading of its own; each of its four parts is its own bordered box.
 */
const BrandBlock = ({
  content,
  creative,
}: {
  content: CreativeBrandContent;
  creative: CreativeLandscape;
}) => <BrandDetail content={content} comparisons={axisComparisons(creative, content.advertiser)} />;

export const BrandsAnalysis = ({ creative, brands, exporting }: BrandsAnalysisProps) => {
  const { t } = useTranslation('analysis');
  // Ordered by the page's brand ranking so the tabs match the rest of the report, with any
  // brand the agent wrote content for but the ranking never saw kept on the end.
  const ordered = [
    ...brands
      .map((b) => b.name)
      .filter((name) => creative.brands.some((c) => c.advertiser === name)),
    ...creative.brands
      .map((c) => c.advertiser)
      .filter((name) => !brands.some((b) => b.name === name)),
  ];
  const [selected, setSelected] = useState(() => ordered[0] ?? '');
  const byName = new Map(brands.map((brand) => [brand.name, brand]));
  const contentFor = (advertiser: string) =>
    creative.brands.find((c) => c.advertiser === advertiser);

  const header = (
    <AnalysisSubHeader
      title={t('analysisResult.creativeLandscape.brandsAnalysis')}
      subtitle={t('analysisResult.creativeLandscape.brandsAnalysisSubtitle')}
      subtitleTone="meta"
    />
  );

  if (exporting) {
    return (
      <div className="flex flex-col gap-md">
        {header}
        {ordered.map((advertiser) => {
          const content = contentFor(advertiser);
          const brand = byName.get(advertiser);
          return content ? (
            <div key={advertiser} className="flex flex-col gap-sm">
              {/* A PDF has no tab strip to say which brand this is, so the export names
                  each block itself. */}
              <div className="flex items-center gap-2.5">
                <BrandIdentity
                  advertiser={advertiser}
                  domain={brand?.domain ?? null}
                  color={brand?.color ?? 'var(--color-blue-600)'}
                />
                <span className="text-body-xl font-medium text-foreground">{advertiser}</span>
              </div>
              <BrandBlock content={content} creative={creative} />
            </div>
          ) : null;
        })}
      </div>
    );
  }

  // Falls back to the first tab if the stored selection is not in the list, so the panel is
  // never blank while a tab still looks active.
  const active = ordered.includes(selected) ? selected : (ordered[0] ?? '');
  const activeContent = contentFor(active);

  return (
    <div className="flex flex-col gap-md">
      {header}
      <BrandTabs
        ariaLabel={t('analysisResult.creativeLandscape.brands')}
        value={active}
        onValueChange={setSelected}
        tabs={ordered.map((advertiser) => {
          const brand = byName.get(advertiser);
          return {
            value: advertiser,
            label: advertiser,
            icon: (
              <BrandIdentity
                advertiser={advertiser}
                domain={brand?.domain ?? null}
                color={brand?.color ?? 'var(--color-blue-600)'}
              />
            ),
          };
        })}
      />
      {activeContent && <BrandBlock content={activeContent} creative={creative} />}
    </div>
  );
};
